/**
 * Session recorder — append-only log of user prompts.
 *
 * Privacy first:
 *   - Disabled by default. Activated explicitly via `cai learn enable`.
 *   - Stored only in `.cai/.cache/sessions.jsonl` (gitignored).
 *   - Never sent anywhere. Local file only.
 *   - `cai learn forget` deletes the log irreversibly.
 *
 * The recorder is invoked by Claude Code's UserPromptSubmit hook. The hook
 * passes the prompt as JSON on stdin; our binary parses it, decides whether
 * recording is active, and appends if so.
 */
import { appendFileSync, existsSync, mkdirSync, readFileSync, rmSync, statSync, writeFileSync } from "node:fs";
import { dirname, join } from "node:path";

export interface SessionEntry {
  ts: number;
  prompt: string;
  /** How the prompt was captured — e.g. "claude-code-hook" */
  source?: string;
  /**
   * Project manifest type at the time of recording. Used to scope correction
   * clusters per tech stack — "no comments" might be a Python rule but not a
   * Go rule. Optional for backwards compat with older logs.
   */
  stack?: string;
}

const CACHE_DIR = join(".cai", ".cache");
const LOG_FILE = "sessions.jsonl";
const STATE_FILE = "learn-enabled";
const MAX_PROMPT_BYTES = 8 * 1024;

function logPath(projectRoot: string): string {
  return join(projectRoot, CACHE_DIR, LOG_FILE);
}

function statePath(projectRoot: string): string {
  return join(projectRoot, CACHE_DIR, STATE_FILE);
}

/** Whether recording is currently enabled for this project. */
export function isLearnEnabled(projectRoot: string): boolean {
  return existsSync(statePath(projectRoot));
}

/** Activate recording. Creates the cache dir + state marker. */
export function enableLearn(projectRoot: string): void {
  mkdirSync(join(projectRoot, CACHE_DIR), { recursive: true });
  writeFileSync(statePath(projectRoot), new Date().toISOString() + "\n", "utf8");
}

/** Deactivate recording. Existing log is preserved unless `forget` is called. */
export function disableLearn(projectRoot: string): void {
  const path = statePath(projectRoot);
  if (existsSync(path)) rmSync(path);
}

/** Delete all recorded session data. Irreversible. */
export function forgetLearn(projectRoot: string): { deletedBytes: number } {
  let deletedBytes = 0;
  const log = logPath(projectRoot);
  if (existsSync(log)) {
    deletedBytes = statSync(log).size;
    rmSync(log);
  }
  disableLearn(projectRoot);
  return { deletedBytes };
}

/**
 * Append a prompt to the session log.
 * Never throws. Silently ignored if recording is disabled.
 */
export function recordPrompt(
  projectRoot: string,
  prompt: string,
  options: { source?: string; stack?: string } = {}
): void {
  try {
    if (!isLearnEnabled(projectRoot)) return;
    const path = logPath(projectRoot);
    mkdirSync(dirname(path), { recursive: true });

    // Truncate very long prompts to keep the log file manageable
    const trimmed = prompt.length > MAX_PROMPT_BYTES ? prompt.slice(0, MAX_PROMPT_BYTES) + "…" : prompt;
    const entry: SessionEntry = { ts: Date.now(), prompt: trimmed };
    if (options.source) entry.source = options.source;
    if (options.stack) entry.stack = options.stack;

    appendFileSync(path, JSON.stringify(entry) + "\n", "utf8");
  } catch {
    // never fail the hook
  }
}

/** Read all recorded prompts since the given cutoff (or all if not given). */
export function readPrompts(projectRoot: string, opts: { sinceMs?: number } = {}): SessionEntry[] {
  const path = logPath(projectRoot);
  if (!existsSync(path)) return [];

  let raw: string;
  try {
    raw = readFileSync(path, "utf8");
  } catch {
    return [];
  }

  const cutoff = opts.sinceMs ?? 0;
  const entries: SessionEntry[] = [];
  for (const line of raw.split("\n")) {
    if (!line) continue;
    try {
      const parsed = JSON.parse(line) as SessionEntry;
      if (parsed.ts >= cutoff) entries.push(parsed);
    } catch {
      // skip bad lines
    }
  }
  return entries;
}

export function sessionLogStats(projectRoot: string): { entries: number; bytes: number; enabled: boolean } {
  const path = logPath(projectRoot);
  const enabled = isLearnEnabled(projectRoot);
  if (!existsSync(path)) return { entries: 0, bytes: 0, enabled };
  try {
    const raw = readFileSync(path, "utf8");
    const entries = raw.split("\n").filter(Boolean).length;
    const bytes = statSync(path).size;
    return { entries, bytes, enabled };
  } catch {
    return { entries: 0, bytes: 0, enabled };
  }
}
